/**
 * @fileoverview History page for viewing issued certificates with background polling
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { formatAddress } from '@/utils/formatters';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Modal } from '@/components/common/Modal';
import { apiService } from '@/services/api/apiService';
import clsx from 'clsx';

export const HistoryPage = () => {
  const { user } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailToSend, setEmailToSend] = useState('');
  // Search and filters
  const [quickSearch, setQuickSearch] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    certificateId: '',
    txHash: '',
    ipfsHash: '',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });

  // Pagination and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const { t } = useLanguage();
  const { formatDate, getTimezoneOffset } = useDateFormat();

  /**
   * Fetch certificates from REST API
   * @param {boolean} silent - If true, doesn't show loading state
   */
  const fetchCertificates = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await apiService.getCertificates();
      setCertificates(data || []);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('❌ Error fetching certificates:', err);
      if (!silent) setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Initial load and polling setup
  useEffect(() => {
    fetchCertificates();

    // Polling every 30 seconds as fallback (in case WebSocket fails)
    const pollingInterval = setInterval(() => {
      console.log('🔄 Polling refresh (fallback)...');
      fetchCertificates(true);
    }, 30000);

    // WebSocket subscriptions for real-time updates
    // Try all possible tenantId locations in the user object
    const tenantId = user?.tenant?.id || user?.tenant_id || user?.tenantId;

    let subscriptionRef = null;
    let reconnectTimer = null;

    const subscribe = (attempt = 0) => {
      if (!tenantId) {
        console.warn('⚠️ [HistoryPage] No tenantId available — skipping GraphQL subscription');
        return;
      }

      console.log(`📡 [HistoryPage] Subscribing for tenant: ${tenantId} (attempt ${attempt + 1})`);

      Promise.all([
        import('@apollo/client/core'),
        import('../lib/apollo.js')
      ]).then(([{ gql }, { client }]) => {
        const CERTIFICATE_UPDATED_SUBSCRIPTION = gql`
          subscription OnCertificateUpdated($tenantId: String!) {
            certificateUpdated(tenantId: $tenantId) {
              id
              status
              blockchain_tx_hash
              blockchain_minted_at
              ipfs_hash
              updated_at
            }
          }
        `;

        subscriptionRef = client.subscribe({
          query: CERTIFICATE_UPDATED_SUBSCRIPTION,
          variables: { tenantId }
        }).subscribe({
          next: (result) => {
            const data = result.data;
            console.log('🔔 [GraphQL Sub] Update received:', result);
            if (data?.certificateUpdated) {
              console.log('✨ [GraphQL Sub] Certificate updated:', data.certificateUpdated.id, '→', data.certificateUpdated.status);
              fetchCertificates(true);
            }
          },
          error: (err) => {
            console.error('❌ [GraphQL Sub] Subscription error:', err.message);
            // Auto-reconnect with exponential backoff (max 30s)
            const delay = Math.min(3000 * Math.pow(2, attempt), 30000);
            console.log(`🔄 [GraphQL Sub] Reconnecting in ${delay / 1000}s...`);
            reconnectTimer = setTimeout(() => subscribe(attempt + 1), delay);
          },
          complete: () => {
            console.log('✅ [GraphQL Sub] Subscription completed');
          }
        });
        console.log(`✅ [HistoryPage] Subscription active for tenant ${tenantId}`);
      }).catch(err => {
        console.error('❌ [HistoryPage] Failed to load Apollo client:', err);
        // Retry after 5s if Apollo fails to load
        reconnectTimer = setTimeout(() => subscribe(attempt + 1), 5000);
      });
    };

    subscribe();

    // Cleanup function
    return () => {
      clearInterval(pollingInterval);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (subscriptionRef) {
        subscriptionRef.unsubscribe();
      }
    };
  }, [fetchCertificates, user]);

  const handleViewDetail = (cert) => {
    setSelectedCertificate(cert);
    setShowDetailModal(true);
  };

  const handleSendEmail = (cert) => {
    setSelectedCertificate(cert);
    setEmailToSend(cert.recipient_email || cert.email || '');
    setShowEmailModal(true);
  };

  // Filter and sort certificates
  const filteredAndSortedCertificates = useMemo(() => {
    let filtered = [...certificates];

    // Certificate ID (exact match)
    if (advancedFilters.certificateId) {
      const query = advancedFilters.certificateId.trim();
      filtered = filtered.filter(cert =>
        cert.id === query
      );
    }

    // TX Hash (partial, case-insensitive)
    if (advancedFilters.txHash) {
      const query = advancedFilters.txHash.trim().toLowerCase();
      filtered = filtered.filter(cert =>
        cert.blockchain_tx_hash?.toLowerCase().includes(query)
      );
    }

    // IPFS Hash (partial, case-insensitive)
    if (advancedFilters.ipfsHash) {
      const query = advancedFilters.ipfsHash.trim().toLowerCase();
      console.log('🔍 IPFS Filter - Query:', query);
      console.log('🔍 Total certificates before filter:', filtered.length);
      filtered = filtered.filter(cert => {
        const hasMatch = cert.ipfs_hash?.toLowerCase().includes(query);
        if (hasMatch) {
          console.log('✅ Match found:', cert.ipfs_hash);
        }
        return hasMatch;
      });
      console.log('🔍 Certificates after IPFS filter:', filtered.length);
    }


    // Date range filtering — convert local date selection to UTC bounds using tenant timezone.
    // The date input gives 'YYYY-MM-DD' in the user's display timezone, but cert timestamps are UTC.
    // We interpret the selected date as start/end of day in the tenant's timezone.
    const toUTCBounds = (dateStr, isEnd = false) => {
      if (!dateStr) return null;
      const [year, month, day] = dateStr.split('-').map(Number);
      const tz = user?.tenant?.timezone || 'UTC';
      // Use Intl to get the UTC offset for that timezone (use noon to avoid DST edge cases)
      const formatter = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' });
      const parts = formatter.formatToParts(new Date(`${dateStr}T12:00:00Z`));
      const offsetStr = parts.find(p => p.type === 'timeZoneName')?.value || 'GMT+0';
      const raw = offsetStr.replace('GMT', '') || '+0';
      const sign = raw[0] === '-' ? -1 : 1;
      const [h, m = '0'] = raw.replace(/^[+-]/, '').split(':');
      const offsetMinutes = sign * (parseInt(h) * 60 + parseInt(m));
      // Start of day in tenant tz = midnight local = UTC midnight minus offset
      if (!isEnd) return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0) - offsetMinutes * 60000);
      // End of day = 23:59:59.999 local
      return new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999) - offsetMinutes * 60000);
    };

    // Date range - From (start of selected day in tenant timezone)
    if (advancedFilters.dateFrom) {
      const fromUTC = toUTCBounds(advancedFilters.dateFrom, false);
      filtered = filtered.filter(cert => {
        const certDate = new Date(cert.issued_at || cert.created_at);
        return certDate >= fromUTC;
      });
    }

    // Date range - To (end of selected day in tenant timezone)
    if (advancedFilters.dateTo) {
      const toUTC = toUTCBounds(advancedFilters.dateTo, true);
      filtered = filtered.filter(cert => {
        const certDate = new Date(cert.issued_at || cert.created_at);
        return certDate <= toUTC;
      });
    }

    // Status filter
    if (advancedFilters.status !== 'all') {
      filtered = filtered.filter(cert =>
        cert.status === advancedFilters.status
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'date') {
        const dateA = new Date(a.issued_at || a.created_at || 0).getTime();
        const dateB = new Date(b.issued_at || b.created_at || 0).getTime();
        comparison = dateA - dateB;
      }
      else if (sortBy === 'ipfs') {
        const hashA = a.ipfs_hash || '';
        const hashB = b.ipfs_hash || '';
        comparison = hashA.localeCompare(hashB);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [certificates, advancedFilters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAndSortedCertificates.length / itemsPerPage);
  const paginatedCertificates = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedCertificates.slice(start, start + itemsPerPage);
  }, [filteredAndSortedCertificates, currentPage]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [advancedFilters, sortBy, sortOrder]);


  if (loading && certificates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-[#0575E6] border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-500 font-medium">{t('common.loading', 'Cargando certificados...')}</p>
      </div>
    );
  }

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-[#111111] tracking-tight">{t('history.title', 'Historial de Certificados')}</h1>
          <p className="mt-2 text-slate-500 text-lg">{t('history.subtitle', 'Visualiza y gestiona todos los documentos emitidos')}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 px-3 py-1.5 rounded-full border border-slate-100 italic">
          <i className="fas fa-sync-alt spin-slow"></i>
          {t('history.last_update', 'Actualizado')}: {formatDate(lastUpdated, 'time')}
          <span className="ml-1 px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded font-mono not-italic border border-blue-100">{getTimezoneOffset()}</span>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-lg flex items-center gap-3">
          <i className="fas fa-exclamation-circle text-xl"></i>
          <div>
            <p className="font-bold">{t('common.error', 'Error')}</p>
            <p className="text-sm">{error}</p>
          </div>
          <button
            onClick={() => fetchCertificates()}
            className="ml-auto px-4 py-1.5 bg-red-600 text-white rounded-md text-sm font-bold hover:bg-red-700"
          >
            {t('common.retry', 'Reintentar')}
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 mb-8 bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        {/* Top Bar: Filters Toggle + Sort */}
        <div className="flex items-center gap-3">
          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={clsx(
              "px-4 py-3.5 rounded-lg font-medium transition-all flex items-center gap-2",
              showAdvancedFilters
                ? "bg-brand text-white shadow-brand/20 shadow-lg"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            )}
          >
            <i className="fas fa-sliders-h"></i>
            {t('history.advanced_filters', 'Filtros Avanzados')}
          </button>

          {/* Sort Controls */}
          <div className="flex items-center gap-2 ml-auto">
            <select
              className="px-4 py-3.5 rounded-lg border border-slate-200 bg-white text-[#111111] text-sm focus:outline-none focus:ring-2 focus:ring-[#0575E6] cursor-pointer"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">{t('history.date', 'Fecha')}</option>
              <option value="ipfs">{t('history.ipfs_cid', 'IPFS CID')}</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-3.5 rounded-lg border border-slate-200 bg-white text-brand hover:bg-slate-50 hover:border-brand transition-all"
              title={sortOrder === 'asc' ? t('history.ascending', 'Ascendente') : t('history.descending', 'Descendente')}
            >
              <i className={clsx('fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down')}></i>
            </button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="mt-2 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Certificate ID */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.certificate_id', 'ID de Certificado')}
                </label>
                <input
                  type="text"
                  value={advancedFilters.certificateId}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, certificateId: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                  placeholder={t('history.id_placeholder', 'UUID exacto...') }
                />
              </div>

              {/* TX Hash */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.tx_hash', 'Transaction Hash')}
                </label>
                <input
                  type="text"
                  value={advancedFilters.txHash}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, txHash: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                  placeholder={t('history.tx_placeholder', '0x...') }
                />
              </div>

              {/* IPFS Hash */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.ipfs_hash', 'IPFS Hash')}
                </label>
                <input
                  type="text"
                  value={advancedFilters.ipfsHash}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, ipfsHash: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                  placeholder={t('history.ipfs_placeholder', 'Qm...') }
                />
              </div>

              {/* Date From */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.date_from', 'Desde')}
                </label>
                <input
                  type="date"
                  value={advancedFilters.dateFrom}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateFrom: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                />
              </div>

              {/* Date To */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.date_to', 'Hasta')}
                </label>
                <input
                  type="date"
                  value={advancedFilters.dateTo}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateTo: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('history.status', 'Estado')}
                </label>
                <select
                  value={advancedFilters.status}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0575E6]"
                >
                  <option value="all">{t('history.all_statuses', 'Todos')}</option>
                  <option value="pending">{t('history.pending', 'Pendiente')}</option>
                  <option value="completed">{t('history.completed', 'Completado')}</option>
                  <option value="processing_on_chain">{t('history.processing_on_chain', 'Registrando en Blockchain')}</option>
                  <option value="verified_on_chain">{t('history.verified', 'Verificado')}</option>
                  <option value="failed">{t('history.failed', 'Fallido')}</option>
                </select>
              </div>
            </div>

            {/* Clear Filters Button */}
            <button
              onClick={() => {
                setAdvancedFilters({
                  certificateId: '',
                  txHash: '',
                  ipfsHash: '',
                  dateFrom: '',
                  dateTo: '',
                  status: 'all'
                });
                setQuickSearch('');
              }}
              className="mt-4 px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <i className="fas fa-redo mr-2"></i>
              {t('history.clear_all_filters', 'Limpiar Todos los Filtros')}
            </button>
          </div>
        )}

        {/* Active Filters Chips */}
        {Object.values(advancedFilters).some(v => v && v !== 'all') && (
          <div className="flex flex-wrap gap-2">
            {advancedFilters.certificateId && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm flex items-center gap-2">
                ID: {advancedFilters.certificateId.substring(0, 8)}...
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, certificateId: '' })} className="hover:text-purple-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
            {advancedFilters.txHash && (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-2">
                TX: {advancedFilters.txHash.substring(0, 10)}...
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, txHash: '' })} className="hover:text-green-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
            {advancedFilters.ipfsHash && (
              <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm flex items-center gap-2">
                IPFS: {advancedFilters.ipfsHash.substring(0, 10)}...
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, ipfsHash: '' })} className="hover:text-orange-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
            {advancedFilters.dateFrom && (
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-2">
                Desde: {advancedFilters.dateFrom}
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, dateFrom: '' })} className="hover:text-indigo-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
            {advancedFilters.dateTo && (
              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm flex items-center gap-2">
                Hasta: {advancedFilters.dateTo}
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, dateTo: '' })} className="hover:text-indigo-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
            {advancedFilters.status !== 'all' && (
              <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm flex items-center gap-2">
                Estado: {advancedFilters.status}
                <button onClick={() => setAdvancedFilters({ ...advancedFilters, status: 'all' })} className="hover:text-pink-900">
                  <i className="fas fa-times text-xs"></i>
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Results Count */}
      {filteredAndSortedCertificates.length > 0 && (
        <div className="flex items-center gap-3 mb-6 px-1">
          <span className="w-2 h-2 rounded-full bg-brand"></span>
          <span className="text-slate-500 font-medium">
            {t('history.showing', 'Mostrando')} <strong className="text-[#111111]">{paginatedCertificates.length}</strong> {t('common.of_label', 'de')} <strong className="text-[#111111]">{filteredAndSortedCertificates.length}</strong> {t('history.certificates', 'certificados')}
          </span>
        </div>
      )}

      {/* Results */}
      {filteredAndSortedCertificates.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-slate-100">
            <i className="fas fa-search text-2xl text-slate-400"></i>
          </div>
          <h3 className="text-lg font-bold text-[#111111] mb-2">{t('history.no_results', 'No se encontraron certificados')}</h3>
          <p className="text-slate-500 mb-6">{t('history.adjust_filters', 'Intenta ajustar los filtros de búsqueda')}</p>
          <button
            onClick={() => {
              setSearchQuery('');
            }}
            className="px-6 py-2.5 rounded-lg bg-[#0575E6] text-white font-bold hover:bg-[#1d4ed8] transition-all shadow-sm"
          >
            <i className="fas fa-redo mr-2"></i>
            {t('history.clear_filters', 'Limpiar Filtros')}
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => sortBy === 'date' ? setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc') : setSortBy('date')}>
                    <div className="flex items-center gap-2">
                      {t('history.date', 'Fecha / Hora')}
                      {sortBy === 'date' && (<i className={clsx('fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down')}></i>)}
                      <span className="ml-1 px-1.5 py-0.5 bg-blue-50 text-blue-500 rounded font-mono normal-case border border-blue-100 text-[10px]">{getTimezoneOffset()}</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('history.col_beneficiary', 'Beneficiario')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('history.col_issued_by', 'Emitido por')}</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => sortBy === 'ipfs' ? setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc') : setSortBy('ipfs')}>
                    <div className="flex items-center gap-2">
                      {t('history.ipfs_cid', 'IPFS CID')}
                      {sortBy === 'ipfs' && (<i className={clsx('fas', sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down')}></i>)}
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">{t('history.tx_hash', 'Tx Hash')}</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">{t('history.status', 'Estado')}</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase tracking-wider">{t('history.actions', 'Acciones')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paginatedCertificates.map((cert) => (
                  <tr key={cert.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-500" title={cert.id}>{cert.id.substring(0, 8)}...</span>
                        <button onClick={() => navigator.clipboard.writeText(cert.id)} className="text-slate-400 hover:text-[#0575E6] transition-colors">
                          <i className="fas fa-copy text-xs"></i>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3 text-[#111111] font-medium">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-brand">
                          <i className="fas fa-calendar-alt text-sm"></i>
                        </div>
                        <div className="flex flex-col">
                          <span>{formatDate(cert.issued_at || cert.created_at, 'short')}</span>
                          <span className="text-xs text-slate-500">{formatDate(cert.issued_at || cert.created_at, 'time')}</span>
                        </div>
                      </div>
                    </td>
                    {/* Beneficiario */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[#111111]">
                          {cert.beneficiary_first_name
                            ? `${cert.beneficiary_first_name} ${cert.beneficiary_last_name || ''}`.trim()
                            : cert.recipient_name || '—'}
                        </span>
                        <span className="text-xs text-slate-400">{cert.beneficiary_email || cert.recipient_email || '—'}</span>
                      </div>
                    </td>
                    {/* Emitido por */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-[#111111]">
                          {cert.issuer_first_name
                            ? `${cert.issuer_first_name} ${cert.issuer_last_name || ''}`.trim()
                            : '—'}
                        </span>
                        <span className="text-xs text-slate-400">{cert.issuer_email || '—'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {cert.ipfs_hash ? (
                        <div className="flex items-center gap-2">
                          <i className="fas fa-cube text-slate-400 text-xs"></i>
                          <a
                            href={`https://ipfs.io/ipfs/${cert.ipfs_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-[#0575E6] hover:underline"
                            title="Ver en IPFS"
                          >
                            {cert.ipfs_hash.substring(0, 12)}...
                          </a>
                          <button
                            onClick={() => navigator.clipboard.writeText(cert.ipfs_hash)}
                            className="text-slate-300 hover:text-[#0575E6] transition-colors ml-1"
                          >
                            <i className="fas fa-copy text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <span className="text-xs text-slate-400 italic">{t('common.not_available', 'No disponible')}</span>
                          {cert.status === 'processing_on_chain' && (
                            <span className="text-[10px] text-brand animate-pulse">{t('history.uploading_ipfs', 'Subiendo a IPFS...')}</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {cert.blockchain_tx_hash && cert.blockchain_tx_hash !== 'completed' ? (
                        <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 w-fit">
                          <i className="fas fa-link text-slate-400 text-xs"></i>
                          <a
                            href={`https://polygonscan.com/tx/${cert.blockchain_tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs text-brand hover:underline"
                          >
                            {formatAddress(cert.blockchain_tx_hash)}
                          </a>
                          <button
                            onClick={() => navigator.clipboard.writeText(cert.blockchain_tx_hash)}
                            className="text-slate-300 hover:text-brand transition-colors ml-1"
                          >
                            <i className="fas fa-copy text-xs"></i>
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <span className={`text-xs px-2 py-0.5 rounded w-fit font-semibold ${cert.status === 'processing_on_chain' ? 'bg-orange-100 text-orange-600' :
                            cert.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'
                            }`}>
                            {cert.status === 'completed' ? t('history.completed', 'Completado') : t(`status.${cert.status}`, cert.status)}
                          </span>
                          {cert.status === 'processing_on_chain' && (
                            <span className="text-[10px] text-orange-500 animate-pulse">Registrando en Blockchain...</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {(() => {
                        const statusConfig = {
                          verified_on_chain: { label: t('status.verified_on_chain', 'Verificado'), cls: 'bg-green-100 text-green-700', icon: 'fa-check-circle' },
                          processing_on_chain: { label: t('status.processing_on_chain', 'En Blockchain'), cls: 'bg-orange-100 text-orange-600', icon: 'fa-spinner fa-spin' },
                          completed: { label: t('history.completed', 'Completado'), cls: 'bg-slate-100 text-slate-500', icon: 'fa-clock' },
                          pending: { label: t('status.pending', 'Pendiente'), cls: 'bg-blue-100 text-blue-600', icon: 'fa-hourglass-half' },
                          svg_generated: { label: t('status.svg_generated', 'SVG Listo'), cls: 'bg-indigo-100 text-indigo-600', icon: 'fa-file-image' },
                          failed: { label: t('status.failed', 'Fallido'), cls: 'bg-red-100 text-red-600', icon: 'fa-times-circle' },
                        };
                        const cfg = statusConfig[cert.status] || { label: cert.status, cls: 'bg-slate-100 text-slate-400', icon: 'fa-question-circle' };
                        return (
                          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-semibold ${cfg.cls}`}>
                            <i className={`fas ${cfg.icon} text-[10px]`}></i>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetail(cert);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-blue-100/50 text-[#0575E6] hover:bg-blue-200/50 font-medium text-sm transition-colors flex items-center gap-2 border border-blue-200/50"
                        >
                          <i className="fas fa-eye"></i>
                          {t('history.view', 'Ver')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center px-6 py-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                {/* First Page Button */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={clsx(
                    "hidden md:inline-flex px-3 py-2 rounded-lg font-medium transition-all items-center gap-1.5",
                    currentPage === 1
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-700 hover:bg-[#0575E6] hover:text-white border border-slate-200"
                  )}
                >
                  <i className="fas fa-angle-double-left text-sm"></i>
                  {t('pagination.first', 'Primera')}
                </button>

                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage(current => Math.max(1, current - 1))}
                  disabled={currentPage === 1}
                  className={clsx(
                    "px-3 py-2 rounded-lg font-medium transition-all items-center gap-1.5",
                    currentPage === 1
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-700 hover:bg-[#0575E6] hover:text-white border border-slate-200"
                  )}
                >
                  <i className="fas fa-angle-left text-sm"></i>
                  {t('common.previous', 'Anterior')}
                </button>

                {/* Page Indicator */}
                <span className="text-sm text-slate-600 font-medium px-4">
                  {t('common.page', 'Página')} <strong className="text-[#111111]">{currentPage}</strong> {t('common.of', 'de')} <strong className="text-[#111111]">{totalPages}</strong>
                </span>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage(current => Math.min(totalPages, current + 1))}
                  disabled={currentPage === totalPages}
                  className={clsx(
                    "px-3 py-2 rounded-lg font-medium transition-all items-center gap-1.5",
                    currentPage === totalPages
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-700 hover:bg-[#0575E6] hover:text-white border border-slate-200"
                  )}
                >
                  {t('common.next', 'Siguiente')}
                  <i className="fas fa-angle-right text-sm"></i>
                </button>

                {/* Last Page Button */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={clsx(
                    "hidden md:inline-flex px-3 py-2 rounded-lg font-medium transition-all items-center gap-1.5",
                    currentPage === totalPages
                      ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                      : "bg-white text-slate-700 hover:bg-[#0575E6] hover:text-white border border-slate-200"
                  )}
                >
                  {t('pagination.last', 'Última')}
                  <i className="fas fa-angle-double-right text-sm"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal Ver Detalle */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
        {selectedCertificate && (
          <div className="w-full max-w-4xl text-left">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h3 className="text-2xl font-bold text-[#111111]">{t('history.document_detail', 'Certificado Emitido')}</h3>
                <p className="text-slate-500 text-sm mt-1">
                  ID: {selectedCertificate.id}
                </p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-slate-600">
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>

            {/* Dates row */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                  <i className="fas fa-calendar-plus mr-1"></i>
                  {t('history.issued_at', 'Fecha de Emisión')}
                </p>
                <p className="text-sm font-semibold text-[#111111]">
                  {formatDate(selectedCertificate.issued_at || selectedCertificate.created_at, 'medium')}
                </p>
                <p className="text-xs text-slate-400 mt-0.5 font-mono">{getTimezoneOffset()}</p>
              </div>
              {selectedCertificate.blockchain_minted_at && (
                <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                  <p className="text-xs font-bold text-green-500 uppercase tracking-wider mb-1">
                    <i className="fas fa-cube mr-1"></i>
                    {t('history.minted_at', 'Minteado en Blockchain')}
                  </p>
                  <p className="text-sm font-semibold text-green-700">
                    {formatDate(selectedCertificate.blockchain_minted_at, 'medium')}
                  </p>
                  <p className="text-xs text-green-400 mt-0.5 font-mono">{getTimezoneOffset()}</p>
                </div>
              )}
            </div>

            <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200 flex flex-col items-center justify-center min-h-[400px]">
              {selectedCertificate.svg_url ? (
                <img
                  src={selectedCertificate.svg_url}
                  alt="Certificate Preview"
                  className="max-w-full h-auto shadow-lg border border-slate-200"
                />
              ) : (
                <div className="text-center text-slate-400">
                  <i className="fas fa-image text-4xl mb-2"></i>
                  <p>Vista previa no disponible</p>
                </div>
              )}
            </div>

            {selectedCertificate.blockchain_tx_hash && (
              <div className="mb-6">
                <label className="text-xs font-semibold text-slate-500 mb-1.5 block ml-1">{t('history.tx_hash', 'Hash de Transacción')}</label>
                <div className="flex items-center gap-2 bg-[#111111] rounded-lg p-3 group hover:ring-2 hover:ring-[#0575E6]/50 transition-all">
                  <i className="fas fa-link text-slate-400 text-xs"></i>
                  <a
                    href={`https://polygonscan.com/tx/${selectedCertificate.blockchain_tx_hash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-slate-300 font-mono flex-1 break-all hover:text-white"
                  >
                    {selectedCertificate.blockchain_tx_hash}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <style dangerouslySetInnerHTML={{
        __html: `
        .spin-slow {
          animation: spin 3s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}} />
    </div>
  );
};
